import React, { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import FollowButton from '../Components/FollowButton'
import Footer from '../Components/Footer'
import { ChevronRight, CloseIcon, HistoryIcon, SearchIcon, Spinner } from '../Components/Icons'
import { useRail } from '../Components/Layout'
import { SearchGroups, searchResultsPath } from '../Components/SearchBox'
import { SourceAvatar } from '../Components/SourceAvatar'
import StoryCard from '../Components/StoryCard'
import { TopicRow } from '../Components/Trending'
import { useDocumentTitle } from '../Hooks/useDocumentTitle'
import {
  clearRecentSearches,
  readRecentSearches,
  removeRecentSearch,
  saveRecentSearch,
  logSearchQuery,
  logSearchResult,
  useSearch,
} from '../Hooks/useSearch'
import { t } from '../i18n'
import { useCategories, useSources, useTrending } from '../State/refData'
import { useUi } from '../State/Ui'
import { norm } from '../Utils/text'

const TOPICS_SHOWN = 5
const TOPICS_MAX = 15
const POPULAR_SOURCES = 10

/* Mobile: like the app's search (recent searches, grouped results while
   typing, follow the words, all results). */
function MobileSearchResults({ value, onPick }) {
  const navigate = useNavigate()
  const { query, data, loading } = useSearch(value)
  const text = value.trim()
  if (text.length < 2) return null
  const nothing =
    data &&
    !data.topics.length &&
    !data.categories.length &&
    !data.sources.length &&
    !data.stories.length
  return (
    <div className="explore-results">
      <div className="explore-follow-row">
        <FollowButton kind="keyword" item={{ title: text }} variant="list" entry="search" />
        <span className="explore-follow-text">{t('search.followQuery', { query: text })}</span>
      </div>
      {loading && !data && (
        <div className="load-more">
          <Spinner size={20} />
        </div>
      )}
      {data && <SearchGroups data={data} query={text} onOpen={() => onPick(text)} />}
      {data?.stories?.length > 0 && (
        <div className="search-group">
          <div className="search-group-title">
            {t('search.news')} · {t('search.storyTotal', { count: data.storyTotal })}
          </div>
          {data.stories.map((s, i) => (
            // A link in the card (headline, source, story) opens the result.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              className="feed-item"
              key={s.id}
              onClick={(e) => e.target.closest('a') && logSearchResult(text, data, 'story', i + 1)}
            >
              <StoryCard story={s} entry="quick_results" position={i + 1} />
            </div>
          ))}
        </div>
      )}
      {nothing && !loading && <p className="search-empty">{t('search.noResults', { query })}</p>}
      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={() => {
          onPick(text)
          logSearchQuery(text, data)
          navigate(searchResultsPath(text))
        }}
      >
        {t('search.allNewsFor', { query: text })}
      </button>
    </div>
  )
}

function RecentSearches({ items, onPick, onChange }) {
  if (!items.length) return null
  return (
    <section className="explore-section">
      <div className="section-head">
        <h2 className="section-title">{t('search.recent')}</h2>
        <button type="button" className="btn-plain" onClick={() => onChange(clearRecentSearches())}>
          {t('search.clearRecent')}
        </button>
      </div>
      <ul className="recent-list">
        {items.map((q) => (
          <li key={q}>
            <button type="button" className="recent-item" onClick={() => onPick(q)}>
              <HistoryIcon size={18} className="muted-icon" />
              <span>{q}</span>
            </button>
            <button
              type="button"
              className="icon-button"
              aria-label={t('search.removeRecent', { query: q })}
              onClick={() => onChange(removeRecentSearch(q))}
            >
              <CloseIcon size={16} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default function Explore() {
  const { isMobile } = useUi()
  const [params] = useSearchParams()
  const [value, setValue] = useState('')
  const [focused, setFocused] = useState(false)
  const [expanded, setExpanded] = useState(params.get('teme') === 'sve')
  const [recent, setRecent] = useState(readRecentSearches)
  const input = useRef(null)
  useRail({ empty: true })
  useDocumentTitle(t('nav.explore'))

  const trending = useTrending({ limit: TOPICS_MAX })
  const categories = useCategories()
  const { sources } = useSources()

  const text = value.trim()
  const key = norm(text)
  const filtering = !isMobile && key.length > 0
  const match = (...names) => !filtering || names.some((n) => norm(n).includes(key))

  const topics = useMemo(
    () => trending.topics.filter((tp) => match(tp.title, tp.shortTitle)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trending.topics, key, filtering]
  )
  const shownTopics =
    filtering || expanded ? topics.slice(0, TOPICS_MAX) : topics.slice(0, TOPICS_SHOWN)
  const shownCategories = categories.filter((c) => match(c.title))
  const shownSources = filtering
    ? sources.filter((s) => match(s.name))
    : sources.slice(0, POPULAR_SOURCES)
  const moreCount = Math.min(TOPICS_MAX, topics.length) - TOPICS_SHOWN

  const pickRecent = (q) => {
    setValue(q)
    setRecent(saveRecentSearch(q))
    input.current?.focus()
  }

  const showMobileResults = isMobile && text.length > 0
  const showRecent = isMobile && focused && !text
  const nothing =
    filtering && !shownTopics.length && !shownCategories.length && !shownSources.length

  return (
    <div className="explore">
      <h1 className="sr-only">{t('nav.explore')}</h1>
      <div className="sticky-bar explore-search">
        <form
          role="search"
          className="search-field"
          onSubmit={(e) => {
            e.preventDefault()
            if (text.length >= 2) {
              setRecent(saveRecentSearch(text))
              input.current?.blur()
            }
          }}
        >
          <SearchIcon size={18} className="search-field-icon" />
          <input
            ref={input}
            type="search"
            value={value}
            placeholder={t('search.placeholder')}
            aria-label={t('search.label')}
            autoComplete="off"
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
          />
          {value && (
            <button
              type="button"
              className="icon-button search-clear"
              aria-label={t('common.clear')}
              onClick={() => {
                setValue('')
                input.current?.focus()
              }}
            >
              <CloseIcon size={16} />
            </button>
          )}
        </form>
      </div>

      {showRecent && <RecentSearches items={recent} onPick={pickRecent} onChange={setRecent} />}
      {showMobileResults ? (
        <MobileSearchResults value={value} onPick={(q) => setRecent(saveRecentSearch(q))} />
      ) : (
        <>
          {filtering && text.length >= 2 && (
            <Link
              to={searchResultsPath(text)}
              className="search-row search-row-news explore-news-row"
            >
              <SearchIcon size={18} />
              <span className="search-row-name">{t('search.searchNewsFor', { query: text })}</span>
            </Link>
          )}
          {nothing && <p className="search-empty">{t('explore.noMatches', { query: text })}</p>}

          {shownTopics.length > 0 && (
            <section className="explore-section">
              <h2 className="section-title">{t('explore.trending')}</h2>
              <ol className="topic-list">
                {shownTopics.map((topic, i) => (
                  <TopicRow key={topic.slug} topic={topic} rank={i + 1} follow entry="explore" />
                ))}
              </ol>
              {!filtering && moreCount > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary btn-block"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded
                    ? t('explore.showLess')
                    : t('explore.showMoreTopics', { count: Math.min(10, moreCount) })}
                </button>
              )}
            </section>
          )}

          {shownCategories.length > 0 && (
            <section className="explore-section">
              <h2 className="section-title">{t('explore.categories')}</h2>
              <div className="category-grid">
                {shownCategories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/kategorija/${c.slug}`}
                    state={{ entry: 'explore' }}
                    className="category-tile"
                  >
                    <span>{c.title}</span>
                    <ChevronRight size={16} className="muted-icon" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {shownSources.length > 0 && (
            <section className="explore-section">
              <div className="section-head">
                <h2 className="section-title">{t('explore.popularSources')}</h2>
                <Link to="/izvori" className="link-orange">
                  {t('explore.allSources', { count: sources.length })}
                  <ChevronRight size={16} strokeWidth={2.5} />
                </Link>
              </div>
              <div className="chip-wrap">
                {shownSources.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/izvor/${s.slug}`}
                    state={{ entry: 'explore' }}
                    className="chip source-chip"
                  >
                    <SourceAvatar source={s} size={22} />
                    <span>{s.name}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
      <Footer className="explore-footer" />
    </div>
  )
}
